import { format, parseISO, setDate, startOfMonth, subMonths } from 'date-fns'
import type { RateCacheEntry } from '@/types/domain'
import { fetchSerie10813Range, type BcbSgsPoint } from '@/core/transport/bcb-sgs-client'
import { putRates } from '@/core/storage/rate-cache'

export const BCB_SERIES_USD_COMPRA = '10813' as const

export interface PtaxDateResolution {
  /** Última data publicada no intervalo [01, 15] do mês anterior. */
  date: string
  /** PTAX compra (USD→BRL) para essa data. */
  ptaxCompra: number
  /** Intervalo efetivamente buscado (para auditoria). */
  searchedRange: { start: string; end: string }
  /** Datas com publicação retornadas pelo BCB no intervalo. */
  publishedDatesInRange: string[]
}

export type PtaxDateResolverError =
  | { code: 'PARAMETRO_INVALIDO'; field: string; detail: string }
  | { code: 'SEM_PUBLICACAO_NO_INTERVALO'; range: { start: string; end: string } }
  | { code: 'API_INDISPONIVEL'; cause: unknown }

export type PtaxResolveOutcome =
  | { ok: true; value: PtaxDateResolution }
  | { ok: false; error: PtaxDateResolverError }

export interface PtaxDateResolver {
  resolve(receiptDateIso: string): Promise<PtaxResolveOutcome>
}

export interface PtaxDateResolverDeps {
  fetchRange?: (start: string, end: string) => Promise<BcbSgsPoint[]>
  cachePut?: (entries: RateCacheEntry[]) => void
  now?: () => Date
}

/**
 * Computes "último dia útil da primeira quinzena do mês anterior ao
 * recebimento" by querying BCB SGS for the [day 01, day 15] interval of the
 * previous month and selecting the last published date. Per decision D-02
 * the BCB publication is the source of truth for "dia útil bancário" — no
 * separate holiday calendar is required.
 */
export function createPtaxDateResolver(deps: PtaxDateResolverDeps = {}): PtaxDateResolver {
  const fetchRange = deps.fetchRange ?? fetchSerie10813Range
  const cachePut = deps.cachePut ?? putRates
  const now = deps.now ?? ((): Date => new Date())

  return {
    async resolve(receiptDateIso: string): Promise<PtaxResolveOutcome> {
      const parsed = parseReceiptDate(receiptDateIso, now())
      if (!parsed.ok) return parsed

      const range = intervalForReceipt(receiptDateIso)

      let points: BcbSgsPoint[]
      try {
        points = await fetchRange(range.start, range.end)
      } catch (err) {
        return { ok: false, error: { code: 'API_INDISPONIVEL', cause: err } }
      }

      if (points.length === 0) {
        return {
          ok: false,
          error: { code: 'SEM_PUBLICACAO_NO_INTERVALO', range },
        }
      }

      const fetchedAt = now().toISOString()
      cachePut(
        points.map((p) => ({
          source: 'bcb',
          series: BCB_SERIES_USD_COMPRA,
          date: p.date,
          value: p.value,
          effectiveDate: p.date,
          fetchedAt,
        })),
      )

      const lastPoint = points[points.length - 1] as BcbSgsPoint
      return {
        ok: true,
        value: {
          date: lastPoint.date,
          ptaxCompra: lastPoint.value,
          searchedRange: range,
          publishedDatesInRange: points.map((p) => p.date),
        },
      }
    },
  }
}

function parseReceiptDate(
  iso: string,
  today: Date,
): { ok: true } | { ok: false; error: PtaxDateResolverError } {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return {
      ok: false,
      error: { code: 'PARAMETRO_INVALIDO', field: 'receiptDate', detail: 'formato esperado YYYY-MM-DD' },
    }
  }
  if (iso < '2020-01-01') {
    return {
      ok: false,
      error: { code: 'PARAMETRO_INVALIDO', field: 'receiptDate', detail: 'data anterior a 2020-01-01' },
    }
  }
  const todayIso = format(today, 'yyyy-MM-dd')
  if (iso > todayIso) {
    return {
      ok: false,
      error: { code: 'PARAMETRO_INVALIDO', field: 'receiptDate', detail: 'data futura' },
    }
  }
  return { ok: true }
}

/** Computes the [01, 15] interval of the month preceding `receiptDateIso`. */
export function intervalForReceipt(receiptDateIso: string): {
  start: string
  end: string
} {
  const receipt = parseISO(receiptDateIso)
  const previous = subMonths(receipt, 1)
  return {
    start: format(startOfMonth(previous), 'yyyy-MM-dd'),
    end: format(setDate(previous, 15), 'yyyy-MM-dd'),
  }
}

export const defaultPtaxDateResolver: PtaxDateResolver = createPtaxDateResolver()
